import type { OrdersTranslations } from './types';

/**
 * Arabic translations for Orders domain
 * Comprehensive translations covering all UI text
 */
export const arTranslations: OrdersTranslations = {
  // Page/Section level
  title: 'الطلبات',
  subtitle: 'تتبع وإدارة طلبات الأسمنت',
  overview: 'نظرة عامة',

  // Actions
  addOrder: 'إضافة طلب',
  editOrder: 'تعديل الطلب',
  deleteOrder: 'حذف الطلب',
  createOrder: 'إنشاء طلب',
  newOrder: 'طلب جديد',
  cancel: 'إلغاء الطلب',
  cancelOrder: 'إلغاء الطلب',
  cancelConfirmTitle: 'إلغاء الطلب',
  cancelConfirmDescription:
    'هل أنت متأكد من إلغاء الطلب {{orderNumber}}؟ لا يمكن التراجع عن هذا الإجراء.',
  cancelSuccessDesc: 'تم إلغاء الطلب {{orderNumber}}.',
  cancelError: 'فشل الإلغاء',
  cancelErrorDesc: 'تعذر إلغاء الطلب {{orderNumber}}. يرجى المحاولة مرة أخرى.',
  orderNumberCopied: 'تم نسخ رقم الطلب',
  copyOrderNumber: 'نسخ رقم الطلب',
  sendSmsToDriver: 'إرسال رسالة نصية للسائق',
  sendingSms: 'جارٍ الإرسال...',
  smsSent: 'تم الإرسال',
  smsSentDesc: 'تم إشعار السائق برابط الطلب.',
  smsError: 'فشل الإرسال',
  smsErrorDesc: 'تعذر إرسال الرسالة النصية. يرجى المحاولة مرة أخرى.',
  smsNoDriver: 'لا يوجد سائق مخصص لهذا الطلب.',
  downloadPDF: 'تحميل تفاصيل الطلب',
  downloadQrPDF: 'تحميل رمز الاستجابة السريعة',
  downloadDeliveryNote: 'تحميل إشعار التسليم',
  previewDeliveryNote: 'معاينة إشعار التسليم',
  trackOrder: 'تتبع الطلب',
  reorder: 'إعادة الطلب',

  // Search and filters
  search: 'البحث في الطلبات...',
  searchPlaceholder: 'البحث برقم الطلب أو رقم أمر المبيعات SAP...',
  noItemsFound: 'لم يتم العثور على طلبات',
  noItemsDescription: 'لم تقم بإضافة أي طلبات بعد. أضف طلبك الأول للبدء.',
  addFirst: 'أضف طلبك الأول',
  noOrders: 'لم يتم العثور على طلبات',
  noOrdersDescription: 'لا توجد طلبات تطابق التصفية الحالية.',

  // Status labels
  pending: 'قيد الانتظار',
  processing: 'قيد المعالجة',
  inTransit: 'في الطريق',
  delivered: 'تم التسليم',
  cancelled: 'ملغي',
  total: 'الإجمالي',
  recent: 'الأخيرة',
  enRoute: 'في الطريق',

  // Order details
  id: 'رقم الطلب',
  sapSalesOrderNumber: 'رقم أمر SAP',
  orderId: 'معرف الطلب',
  orderDetails: 'تفاصيل الطلب',
  amount: 'المبلغ',
  quantity: 'الكمية',
  schedule: 'الجدول الزمني',
  orderDate: 'تاريخ الطلب',
  deliveryDate: 'تاريخ التسليم',
  processingToDeliveredTime: 'مدة المعالجة حتى التسليم',
  loadingTime: 'وقت التحميل',
  deliveryAddress: 'عنوان التسليم',
  paymentStatus: 'حالة الدفع',
  paid: 'مدفوع',
  salesOrder: 'طلب المبيعات',
  viewSalesOrder: 'عرض تفاصيل طلب المبيعات',
  salesOrderCreatedBy: 'أنشئ طلب المبيعات بواسطة',
  product: 'المنتج',
  driver: 'السائق',
  factory: 'المصنع',
  bags: 'أكياس',
  tons: 'طن',
  kg: 'كجم',
  bulk: 'سائب',
  selectProduct: 'اختر المنتج',
  trackingInfo: 'معلومات التتبع',
  driverInfo: 'معلومات السائق',
  notFound: 'الطلب غير موجود',
  orderNotFoundDescription: 'تعذر العثور على الطلب',

  // Weighbridge
  shipment: 'الشحنة',
  weighbridge: 'الميزان',
  weighbridgeGross: 'الوزن الإجمالي',
  weighbridgeTare: 'وزن الفارغ',
  weighbridgeNet: 'الوزن الصافي',
  weighbridgeTickets: 'التذاكر',
  weighbridgeTimeline: 'سجل الميزان',

  // PDF
  downloadingPDF: 'جاري تحميل PDF',
  downloadingPDFDesc: 'جاري إنشاء PDF للطلب {{orderNumber}}...',
  pdfDownloaded: 'تم تحميل PDF',
  pdfDownloadedDesc: 'تم تحميل PDF للطلب {{orderNumber}}',
  pdfDownloadError: 'فشل التحميل',
  pdfDownloadErrorDesc: 'فشل في تحميل PDF الطلب. يرجى المحاولة مرة أخرى.',
  downloadingDeliveryNote: 'جاري تحميل إشعار التسليم',
  downloadingDeliveryNoteDesc: 'جاري إنشاء إشعار التسليم للطلب {{orderNumber}}...',
  deliveryNoteDownloaded: 'تم تحميل إشعار التسليم',
  deliveryNoteDownloadedDesc: 'تم تحميل إشعار التسليم للطلب {{orderNumber}}',
  deliveryNoteDownloadError: 'فشل تحميل إشعار التسليم',
  deliveryNoteDownloadErrorDesc: 'فشل في تحميل إشعار التسليم. يرجى المحاولة مرة أخرى.',
  pdfPreviewTitle: 'ملف PDF للطلب',
  pdfPreviewSubtitle: 'معاينة آمنة داخل التطبيق',
  pdfPreviewShare: 'مشاركة',
  pdfPreviewRetry: 'حاول مرة أخرى',
  pdfPreviewFileName: 'اسم الملف',
  pdfPreviewFileSize: 'حجم الملف',
  pdfPreviewDismiss: 'إلغاء',
  pdfPreviewBadge: 'PDF',
  pdfPreviewReady: 'الملف جاهز',
  pdfPreviewReadyDesc: 'يمكنك مراجعة ومشاركة ملف PDF للطلب.',
  pdfPreviewUnavailableTitle: 'المعاينة غير متاحة',
  pdfPreviewUnavailableDesc:
    'لا يمكن لهذا الجهاز عرض ملفات PDF داخل التطبيق. استخدم المشاركة لفتح الملف.',
  pdfShareErrorDesc: 'تعذر مشاركة ملف PDF للطلب. يرجى المحاولة مرة أخرى.',
  pdfShareUnavailableDesc: 'المشاركة غير متاحة على هذا الجهاز.',
  deliveryNotePreviewTitle: 'إشعار التسليم',
  deliveryNotePreviewSubtitle: 'معاينة ومشاركة من هذا الجهاز',
  deliveryNotePreviewDocumentName: 'اسم المستند',
  deliveryNotePreviewDocumentSize: 'حجم المستند',
  deliveryNotePreviewBadge: 'تسليم',
  deliveryNotePreviewReady: 'إشعار التسليم جاهز',
  deliveryNotePreviewReadyDesc: 'يمكنك مراجعة ومشاركة إشعار التسليم.',
  deliveryNoteShareErrorDesc: 'تعذر مشاركة إشعار التسليم. يرجى المحاولة مرة أخرى.',
  pdfFileSizeUnitB: 'B',
  pdfFileSizeUnitKB: 'KB',
  pdfFileSizeUnitMB: 'MB',
  pdfFileSizeUnitGB: 'GB',

  // Filters
  filterByStatus: 'التصفية حسب الحالة',
  filterByProduct: 'التصفية حسب المنتج',
  filterByAmount: 'التصفية حسب المبلغ',
  filterByAddress: 'التصفية حسب العنوان',
  filterByDate: 'التصفية حسب التاريخ',
  filterByOrderDate: 'التصفية حسب تاريخ الطلب',
  filterByDeliveryDate: 'التصفية حسب تاريخ التسليم',
  filterByLP: 'التصفية حسب رقم اللوحة',
  lpPlaceholder: 'أدخل رقم اللوحة',
  filterByDriver: 'التصفية حسب السائق',
  driverPlaceholder: 'أدخل اسم السائق',
  driverNameExamplePlaceholder: 'مثال: أحمد',
  filterByQuantity: 'التصفية حسب الكمية',
  quantityFilterHint: 'بوحدة كل طلب (أكياس أو أطنان أو كجم)',
  filterByLoadingTime: 'التصفية حسب وقت التحميل',
  filterByProcessingTime: 'التصفية حسب وقت المعالجة',
  durationFilterHint: 'يتم استبعاد الطلبات التي ليس لها مدة مسجلة',
  minutesUnit: 'دقيقة',
  filterDescription: 'تحسين قائمة الطلبات باستخدام هذه الفلاتر',
  searchProducts: 'البحث عن المنتجات...',
  searchAddresses: 'البحث عن العناوين...',
  minAmount: 'الحد الأدنى للمبلغ',
  maxAmount: 'الحد الأقصى للمبلغ',
  noLimit: 'بدون حد',
  selectDateRange: 'اختر نطاق التاريخ',
  selectStatus: 'اختر الحالة',

  // Sort options
  sortByDate: 'التاريخ',
  sortNewest: 'الأحدث أولاً',
  sortOldest: 'الأقدم أولاً',
  sortByOrderNumber: 'رقم الطلب',
  sortByOrderNumberAsc: 'رقم الطلب (أ-ي)',
  sortByOrderNumberDesc: 'رقم الطلب (ي-أ)',
  sortByOrderDate: 'تاريخ الطلب',
  sortByOrderDateAsc: 'تاريخ الطلب (الأقدم)',
  sortByOrderDateDesc: 'تاريخ الطلب (الأحدث)',
  sortByDeliveryDate: 'تاريخ التسليم',
  sortByDeliveryDateAsc: 'التسليم (الأقرب)',
  sortByDeliveryDateDesc: 'التسليم (الأبعد)',
  sortByAmount: 'المبلغ',
  sortByAmountAsc: 'المبلغ (الأقل إلى الأعلى)',
  sortByAmountDesc: 'المبلغ (الأعلى إلى الأقل)',
  sortByStatus: 'الحالة',
  sortByStatusAsc: 'الحالة (أ-ي)',
  sortByStatusDesc: 'الحالة (ي-أ)',
  sortByProduct: 'المنتج',
  sortByProductAsc: 'المنتج (أ-ي)',
  sortByProductDesc: 'المنتج (ي-أ)',
  sortByTenant: 'المستأجر',
  sortByTenantAsc: 'المستأجر (أ-ي)',
  sortByTenantDesc: 'المستأجر (ي-أ)',

  // New order modal
  addressPlaceholder: 'اختر عنوان التسليم',
  notes: 'ملاحظات',
  notesPlaceholder: 'تعليمات خاصة...',
  estimatedTotal: 'الإجمالي التقديري',

  // Form section
  form: {
    name: 'اسم الطلب',
    namePlaceholder: 'أدخل اسم الطلب',
    status: 'الحالة',
    validation: {
      nameRequired: 'اسم الطلب مطلوب',
    },
  },

  // Status (kept for compatibility)
  status: {
    active: 'نشط',
    inactive: 'غير نشط',
  },

  // Toast messages
  messages: {
    createSuccess: 'تم إنشاء الطلب بنجاح',
    updateSuccess: 'تم تحديث الطلب بنجاح',
    deleteSuccess: 'تم حذف الطلب بنجاح',
    deleteConfirm: 'هل أنت متأكد من حذف هذا الطلب؟',
  },

  // Table headers
  table: {
    name: 'الاسم',
    status: 'الحالة',
    factory: 'المصنع',
    product: 'المنتج',
    driver: 'السائق',
    quantity: 'الكمية',
    orderDate: 'تاريخ الطلب',
    deliveryDate: 'تاريخ التسليم',
    amount: 'المبلغ',
    actions: 'الإجراءات',
  },

  // Table column labels
  configureColumns: 'الأعمدة',
  selectVisibleColumns: 'اختر الأعمدة الظاهرة',
  actions: 'الإجراءات',
  cancelReasonUserRequested: 'طلب المستخدم الإلغاء',

  // Top-level convenience keys
  name: 'الاسم',
  namePlaceholder: 'أدخل اسم الطلب',
  statusLabel: 'الحالة',
  nameRequired: 'اسم الطلب مطلوب',
  active: 'نشط',
  inactive: 'غير نشط',
  createSuccess: 'تم إنشاء الطلب بنجاح',
  updateSuccess: 'تم تحديث الطلب بنجاح',
  deleteSuccess: 'تم حذف الطلب بنجاح',
  deleteConfirm: 'هل أنت متأكد من حذف هذا الطلب؟',

  // Bulk actions
  bulkActions: {
    selected: 'محدد',
    selectAllOnPage: 'تحديد الكل في هذه الصفحة',
    deselectAllOnPage: 'إلغاء تحديد الكل في هذه الصفحة',
    selectAllOrders: 'تحديد جميع الطلبات ({{count}})',
    clearSelection: 'مسح التحديد',
    downloadPDF: 'تحميل PDF',
    downloadingPDFs: 'جاري تحميل ملفات PDF',
    preparingPDFs: 'جاري إعداد PDF لـ {{count}} طلبات...',
    pdfsDownloaded: 'تم تحميل ملفات PDF',
    pdfsDownloadedDesc: 'تم تحميل PDF لـ {{count}} طلبات بنجاح',
    pdfDownloadErrorDesc: 'فشل في تحميل ملفات PDF. يرجى المحاولة مرة أخرى.',
    maxOrdersExceeded: 'يمكن تحميل 25 طلب كحد أقصى في المرة الواحدة',
    cancelSelected: 'إلغاء المحدد',
    cancelSelectedSuccess: 'تم إلغاء {{count}} من الطلبات المحددة',
    cancelSelectedPartialTitle: 'اكتمل الإلغاء مع وجود مشاكل',
    cancelSelectedPartialDesc:
      'تم إلغاء {{successCount}} طلبات، وفشل {{failureCount}}',
    cancelSelectedError: 'فشل الإلغاء الجماعي',
    cancelSelectedErrorDesc:
      'تعذر إلغاء الطلبات المحددة. يرجى المحاولة مرة أخرى.',
    selectAllAnyStatus: 'تحديد الكل في هذه الصفحة (أي حالة)',
    deselectAllAnyStatus: 'إلغاء تحديد الكل في هذه الصفحة',
    forceStatusUpdate: 'تحديث الحالة قسراً',
    forceStatusUpdating: 'جاري التحديث...',
    forceStatusUpdateSuccess: 'تم تحديث {{count}} طلب إلى {{status}} قسراً',
    forceStatusUpdatePartialTitle: 'اكتمل التحديث القسري مع وجود مشاكل',
    forceStatusUpdatePartialDesc: 'تم تحديث {{successCount}} طلبات، وفشل {{failureCount}}',
    forceStatusUpdateError: 'فشل التحديث القسري',
    forceStatusUpdateErrorDesc: 'تعذر تحديث حالات الطلبات قسراً. يرجى المحاولة مرة أخرى.',
  },

  forceStatusUpdateModal: {
    title: 'تحديث الحالة قسراً',
    description: 'يتجاوز هذا الإجراء انتقالات الحالة العادية للطلبات. سيتم تسجيله بتفاصيل تدقيق كاملة. استخدمه فقط في حالات الطوارئ أو التصحيح المعتمدة.',
    ordersSummary: '{{count}} طلب (طلبات) محددة',
    targetStatusLabel: 'الحالة المستهدفة',
    targetStatusPlaceholder: 'اختر الحالة المستهدفة',
    reasonLabel: 'السبب (20 حرفًا على الأقل)',
    reasonPlaceholder: 'اشرح بالتفصيل سبب ضرورة هذا التغيير القسري للحالة، وما هو الحادث أو المشكلة التي أدت إليه، ومن أذن بذلك...',
    reasonMinLength: 'يجب أن يكون السبب 20 حرفًا على الأقل',
    incidentRefLabel: 'مرجع الحادث / التذكرة (اختياري)',
    incidentRefPlaceholder: 'مثال: INC-1234، JIRA-567',
    affectedOpsNoteLabel: 'ملاحظات التشغيل (اختياري)',
    affectedOpsNotePlaceholder: 'أي ملاحظات لفريق العمليات بشأن هذا التجاوز...',
    acknowledgeLabel: 'أؤكد أن هذا تجاوز معتمد وأتحمل المسؤولية الكاملة عن هذا الإجراء',
    confirmTextLabel: 'اكتب "FORCE UPDATE" للتأكيد',
    confirmTextPlaceholder: 'FORCE UPDATE',
    confirmTextHint: 'يجب كتابة FORCE UPDATE (بالأحرف الكبيرة) لتفعيل زر التأكيد',
    confirmTextMismatch: 'يجب كتابة "FORCE UPDATE" بالضبط',
    submitButton: 'تحديث {{count}} طلب (طلبات) قسراً',
    submittingButton: 'جاري التحديث...',
    cancelButton: 'إلغاء',
    warningTitle: 'إجراء لا رجعة فيه',
    warningDesc: 'سيؤدي هذا فوراً إلى تغيير حالة {{count}} طلب (طلبات). هذا الإجراء مُسجَّل بالكامل وقابل للتدقيق.',
    statusPlanned: 'مخطط (قيد الانتظار)',
    statusProcessing: 'قيد المعالجة',
    statusDelivered: 'تم التسليم',
    statusCanceled: 'ملغى',
    statusFailed: 'فشل',
    currentStatusSummaryLabel: 'الحالات الحالية للطلبات المحددة',
  },

  // Timeline tab
  timeline: 'السجل الزمني',
  timelineEmpty: 'لا توجد أحداث بعد',
  timelineEmptyDesc: 'ستظهر الأحداث هنا مع تقدم الطلب',
  timelineEntityOrder: 'الطلب',
  timelineEntityShipment: 'الشحنة',
  timelineEntityWeighbridge: 'الميزان',
  timelineEntityTransaction: 'معاملة مالية',
  timelineEntityWeightCorrection: 'تصحيح الوزن',
  timelineEntityQueueEntry: 'إدخال القائمة',
  timelineEntitySalesOrder: 'أمر البيع',
  timelineEntityTenant: 'المستأجر',
  timelineEntityUser: 'المستخدم',
  timelineEntityCustomerAccount: 'حساب العميل',
  timelineEntityVariant: 'الصنف',
  timelineEntitySku: 'رمز المنتج',
  timelineEntityCategory: 'الفئة',
  timelineEntityLoadingLocation: 'موقع التحميل',
  timelineActionCreated: 'تم الإنشاء',
  timelineActionUpdated: 'تم التحديث',
  timelineActionDeleted: 'تم الحذف',
  timelineActionStatusChanged: 'تغيير الحالة',
  timelineActionResourceAssigned: 'تعيين المورد',
  timelineActionResourceChanged: 'تغيير المورد',
  timelineActionWeighed: 'تم الوزن',
  timelineActionWeightCorrected: 'تصحيح الوزن',
  timelineActionGateOpened: 'فتح البوابة',
  timelineActionWeighbridgeTriggered: 'تشغيل الميزان',
  timelineActionApproved: 'تمت الموافقة',
  timelineActionSuspended: 'تم التعليق',
  timelineActionActivated: 'تم التفعيل',
  timelineActionBlocked: 'تم الحظر',
  timelineActionUnblocked: 'تم رفع الحظر',
  timelineActionPasswordReset: 'إعادة تعيين كلمة المرور',
  timelineActionRoleChanged: 'تغيير الدور',
  timelineActionChannelChanged: 'تغيير القناة',
  timelineActionGroupChanged: 'تغيير المجموعة',
  timelineActionKioskScanFailed: 'فشل المسح',

  // Timeline metadata detail labels
  timelineMetaDriver: 'السائق',
  timelineMetaVehicle: 'المركبة',
  timelineMetaTrailer: 'المقطورة',
  timelineMetaNet: 'الصافي',
  timelineMetaFirst: 'الأول',
  timelineMetaSecond: 'الثاني',
  timelineMetaChanged: 'المُغيَّر',
  timelineMetaReason: 'السبب',
  timelineMetaNetWeight: 'الوزن الصافي',
  timelineMetaForcedBy: 'بواسطة (إجباري)',
  timelineMetaIncident: 'مرجع الحادث',
  timelineMetaOpsNote: 'ملاحظة العمليات',
  timelineStatusPlanned: 'مجدول',
  timelineStatusProcessing: 'قيد المعالجة',
  timelineStatusDelivered: 'تم التسليم',
  timelineStatusCanceled: 'ملغي',
  timelineStatusFailed: 'فشل',
  timelineStatusPending: 'قيد الانتظار',
  timelineStatusLoading: 'قيد التحميل',
  timelineStatusInTransit: 'قيد النقل',
  timelineStatusArrived: 'وصل',
  timelineStatusCompleted: 'مكتمل',
  timelineStatusReady: 'جاهز',
  timelineStatusEntered: 'دخل',
  timelineStatusWaiting: 'في الانتظار',
  timelineStageFirstWeight: 'الوزن الأول',
  timelineStageSecondWeight: 'الوزن الثاني',
  timelineFieldOrderStatus: 'حالة الطلب',
  timelineFieldShipmentStatus: 'حالة الشحنة',
  timelineFieldSapSalesOrderNumber: 'رقم أمر البيع في SAP',
  timelineFieldPricingSnapshot: 'لقطة التسعير',
  timelineFieldProcessingStartedAt: 'وقت بدء المعالجة',
  timelineFieldQuantity: 'الكمية',
  timelineFieldDeliveryDate: 'تاريخ التسليم',
  timelineFieldCancellationReason: 'سبب الإلغاء',

  // Audit log event descriptions
  audit: {
    orderCreated: 'تم إنشاء الطلب #{{orderNumber}}',
    orderStatusChanged: 'الطلب #{{orderNumber}}: من {{previousStatus}} إلى {{newStatus}}',
    orderUpdated: 'تم تحديث الطلب #{{orderNumber}}',
    orderDeleted: 'تم حذف الطلب #{{orderNumber}}',
    shipmentCreated: 'تم إنشاء الشحنة {{shipmentNumber}}',
    shipmentCreatedForOrder: 'تم إنشاء الشحنة {{shipmentNumber}} للطلب #{{orderNumber}}',
    shipmentStatusChanged: 'الشحنة {{shipmentNumber}}: من {{previousStatus}} إلى {{newStatus}}',
    shipmentUpdated: 'تم تحديث الشحنة {{shipmentNumber}}',
    shipmentDeleted: 'تم حذف الشحنة {{shipmentNumber}}',
    shipmentResourceAssigned: 'تم تعيين {{resourceName}} للشحنة {{shipmentNumber}}',
    shipmentResourceChanged: 'تم تغيير المورد في الشحنة {{shipmentNumber}}',
    weighbridgeTareRecorded: 'تم تسجيل وزن الفارغ: {{tare}}',
    weighbridgeGrossRecorded: 'تم تسجيل الوزن الإجمالي: {{gross}} — الصافي: {{net}}',
    weightCorrected: 'تم تصحيح الوزن بواسطة {{correctedBy}} على التذكرة {{ticketNumber}}',
    queueTicketIssued: 'تم إصدار تذكرة القائمة {{displayNumber}} في {{locationLabel}}',
    queueTicketCancelled: 'تم إلغاء تذكرة القائمة {{displayNumber}}',
    kioskJoinFailed: 'فشل مسح الكشك',
    kioskErrors: {
      noResourceAssigned: 'لا يوجد مورد مخصص للشحنة',
      alreadyInQueue: 'الطلب موجود بالفعل في القائمة',
      vehiclePlateAlreadyInQueue: 'هذه المركبة موجودة بالفعل في القائمة',
      noLoadingLocation: 'لا توجد نقطة تحميل متاحة',
      sapSalesOrderFailed: 'فشل إنشاء أمر المبيعات في SAP. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.',
      noActiveContract: 'لم يتم العثور على عقد نشط. يرجى التواصل مع مدير حسابك.',
      insufficientContractBalance: 'رصيد العقد غير كافٍ. يرجى التواصل مع مدير حسابك.',
      noSapMaterialCode: 'خطأ في تكوين المنتج. يرجى التواصل مع مدير حسابك.',
    },
    gateOpenedForEntry: 'تم فتح بوابة الدخول — {{gateLabel}}',
    gateOpenedForExit: 'تم فتح بوابة الخروج — {{gateLabel}}',
    weighbridgeFlowTriggered: 'تم الوصول الى الميزان عند البوابة {{gateLabel}} (المرحلة: {{weightStageLabel}})',
    queueEntryEnteredFactory: '{{displayNumber}} دخل المصنع من البوابة {{gateId}} (من {{fromStatus}} إلى {{toStatus}})',
    queueEntryExitedFactory: '{{displayNumber}} خرج من المصنع (من {{fromStatus}} إلى {{toStatus}})',
  },

  errors: {
    cancellationReasonRequired: 'سبب الإلغاء مطلوب',
    onlyPlannedCanBeCancelled: 'يمكن إلغاء الطلبات المجدولة فقط',
    cannotCancelScannedOrder: 'لا يمكن إلغاء الطلبات الممسوحة',
    cannotCancelProcessing: 'لا يمكن إلغاء الطلب بعد بدء المعالجة',
    missingTenant: 'بيانات المستأجر مفقودة في الطلب',
    notFound: 'الطلب غير موجود',
    notPending: 'الطلب ليس في حالة انتظار',
    cancelFailed: 'فشل إلغاء الطلب',
    orderNumberRequired: 'رقم الطلب مطلوب',
  },

  export: {
    exportingTitle: 'جارٍ التصدير...',
    exportingDesc: 'جلب جميع السجلات',
    exportingSelected: 'تصدير {{count}} طلب محدد',
    exportFailedTitle: 'فشل التصدير',
    exportFailed: 'تعذّر تصدير الطلبات',
    emailPromptTitle: 'إلى أي بريد إلكتروني نرسل التصدير؟',
    emailPromptDescription: 'أدخل البريد الإلكتروني لهذا التصدير. التنزيلات الصغيرة تُحمّل مباشرة بعد الإرسال، أما التنزيلات الأكبر فتُرسل عبر البريد الإلكتروني.',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'name@example.com',
    emailRequired: 'البريد الإلكتروني مطلوب',
    emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح',
    exportDownloadedTitle: 'تم تنزيل التصدير',
    exportDownloadedDescription: 'تم تنزيل {{count}} طلب',
    exportEmailedTitle: 'سيتم إرسال التصدير عبر البريد الإلكتروني',
    exportEmailedDescription: 'سنرسل {{count}} طلبًا إلى {{email}}',
  },
  licensePlateExamplePlaceholder: 'مثال: 5080-ZZR',
  subCustomer: 'العميل',
  selectSubCustomer: 'اختر العميل',
  changeSubCustomer: 'تغيير العميل',
  noSubCustomer: 'لم يتم تعيين عميل',
  addNewSubCustomer: 'إضافة عميل جديد',
  subCustomerCode: 'الرمز',
  subCustomerPhone: 'الهاتف',
  sapSync: 'مزامنة SAP',
  sapSyncNoJobs: 'لا توجد مهام مزامنة SAP',
  sapSyncNoJobsDescription: 'لم يتم تسجيل أي عمليات مزامنة SAP لهذا الطلب.',

  supportLookup: {
    title: 'بحث دعم الطلبات',
    subtitle: 'ابحث برقم الطلب أو رقم أمر البيع SAP لعرض تفاصيل الطلب الكاملة',
    searchPlaceholder: 'أدخل رقم الطلب أو رقم SAP...',
    search: 'بحث',
    searching: 'جاري البحث...',
    orderDetails: 'تفاصيل الطلب',
    warningTitle: 'أداة الدعم — تعامل بحذر',
    warningDescription: 'تتيح هذه الصفحة التعديل المباشر على بيانات الطلبات الحية بما في ذلك تذاكر الميزان وبنود التسليم وأحداث الجدول الزمني. جميع التغييرات مسجلة ولا يمكن التراجع عنها. تابع فقط إذا كنت تفهم التأثير تمامًا.',
    confirmCancel: 'إلغاء',
    confirmConfirm: 'تأكيد',
    btnTriggerSap: 'تشغيل أمر مبيعات SAP',
    btnTriggering: 'جاري التشغيل...',
    sapSyncConfirmTitle: 'تشغيل مزامنة أمر مبيعات SAP؟',
    sapSyncConfirmDescription: 'سيؤدي هذا إلى إرسال طلب مزامنة إلى SAP لهذا الطلب. هل أنت متأكد من المتابعة؟',
    fieldOrderNumber: 'رقم الطلب',
    fieldSapSalesOrder: 'أمر مبيعات SAP',
    fieldStatus: 'الحالة',
    fieldQuantity: 'الكمية',
    fieldDeliveryDate: 'تاريخ التسليم',
    fieldProcessingStarted: 'بدء المعالجة',
    fieldTenant: 'المستأجر',
    fieldFactory: 'المصنع',
    fieldProduct: 'المنتج',
    fieldCity: 'المدينة',
    fieldDropoff: 'نقطة التسليم',
    fieldSubCustomer: 'العميل الفرعي',
    fieldSalesOrder: 'أمر البيع',
    fieldCreated: 'تاريخ الإنشاء',
    fieldCancellationReason: 'سبب الإلغاء',
    sectionShipment: 'الشحنة',
    fieldShipmentNumber: 'رقم الشحنة',
    fieldDispatchedAt: 'تاريخ الإرسال',
    fieldArrivedAt: 'تاريخ الوصول',
    fieldOrigin: 'المصدر',
    fieldDestination: 'الوجهة',
    fieldHoldReason: 'سبب التعليق',
    fieldHoldAt: 'تاريخ التعليق',
    sectionResource: 'تعيين المورد',
    fieldResourceName: 'اسم المورد',
    fieldDriver: 'السائق',
    fieldDriverPhone: 'هاتف السائق',
    fieldPowerUnit: 'وحدة القدرة',
    fieldTrailer: 'المقطورة',
    sectionWeighbridge: 'تذاكر الميزان',
    colTicketNumber: 'رقم التذكرة',
    colType: 'النوع',
    colGross: 'الإجمالي',
    colTare: 'التاري',
    colNet: 'الصافي',
    colScale: 'الميزان',
    colMeasuredAt: 'تاريخ القياس',
    colActions: 'الإجراءات',
    weighbridgeConfirmTitle: 'تحديث تذكرة الميزان؟',
    weighbridgeConfirmDescription: 'أنت على وشك تعديل بيانات الميزان. سيؤثر هذا على حسابات الوزن الصافي. هل أنت متأكد؟',
    sectionQueue: 'إدخال قائمة الانتظار',
    fieldQueueNumber: 'رقم قائمة الانتظار',
    fieldLoadingLocation: 'موقع التحميل',
    fieldVehicle: 'المركبة',
    fieldRequestedAt: 'تاريخ الطلب',
    fieldCalledAt: 'تاريخ الاستدعاء',
    fieldEnteredFactory: 'دخول المصنع',
    fieldLoadingStarted: 'بدء التحميل',
    fieldLoadingCompleted: 'اكتمال التحميل',
    fieldExitedFactory: 'خروج المصنع',
    fieldEntryGate: 'بوابة الدخول',
    fieldExitGate: 'بوابة الخروج',
    sectionDeliveryLines: 'بنود التسليم',
    colDeliveryNumber: 'رقم التسليم',
    colDeliveryStatus: 'حالة التسليم',
    colDeliveredQty: 'الكمية المسلمة',
    colLineStatus: 'حالة البند',
    colPackaging: 'التعبئة',
    colCommittedQty: 'الكمية الملتزم بها',
    deliveryConfirmTitle: 'تحديث بند التسليم؟',
    deliveryConfirmDescription: 'أنت على وشك تعديل كمية أو حالة بند التسليم. سيؤثر هذا على الحسابات المالية والتقارير. هل أنت متأكد؟',
    sectionInvoices: 'الفواتير',
    colInvoiceNumber: 'رقم الفاتورة',
    colTotal: 'الإجمالي',
    colPaid: 'المدفوع',
    colSapNumber: 'رقم SAP',
    colIssueDate: 'تاريخ الإصدار',
    colDueDate: 'تاريخ الاستحقاق',
    sectionSapSyncJobs: 'مهام مزامنة SAP',
    colDirection: 'الاتجاه',
    colOperation: 'العملية',
    colError: 'الخطأ',
    colRetries: 'محاولات الإعادة',
    colCreated: 'تاريخ الإنشاء',
    sectionTimeline: 'الجدول الزمني للطلب',
    placeholderAction: 'الإجراء',
    placeholderDescription: 'الوصف',
    eventConfirmTitle: 'تحديث الحدث؟',
    eventConfirmDescription: 'أنت على وشك تعديل حدث الجدول الزمني لهذا الطلب. هل أنت متأكد؟',
    deleteEventConfirmTitle: 'حذف الحدث؟',
    deleteEventConfirmDescription: 'سيؤدي هذا إلى إزالة الحدث نهائيًا من الجدول الزمني للطلب. لا يمكن التراجع عن هذا الإجراء.',
    btnSave: 'حفظ',
    btnCancel: 'إلغاء',
    btnEdit: 'تعديل',
    btnDelete: 'حذف',
  },
  offlineEntry: {
    title: 'إدخال الطلبات دون اتصال',
    subtitle: 'استكمال بيانات الطلبات التي تم وزنها أثناء انقطاع الاتصال بالمصنع. أدخل الأوزان يدويًا أو ارفع ملفًا.',
    tabManual: 'إدخال يدوي',
    tabUpload: 'رفع ملف',
    weightUnitHint: 'الأوزان بالكيلوجرام (كجم)',
    colOrderNumber: 'رقم الطلب',
    colFirstWeight: 'الوزن الأول',
    colSecondWeight: 'الوزن الثاني',
    colDeliveryDate: 'تاريخ التسليم',
    colStatus: 'الحالة',
    colActions: 'إجراءات',
    addRow: 'إضافة صف',
    removeRow: 'حذف',
    submit: 'إرسال',
    submitting: 'جارٍ الإرسال…',
    clear: 'مسح',
    downloadResults: 'تنزيل النتائج',
    dropzone: 'أفلت ملف Excel أو CSV هنا',
    dropzoneHint: 'الأعمدة: orderNumber، firstWeight، secondWeight، deliveryDate',
    browseFile: 'اختيار ملف',
    parseError: 'تعذّرت قراءة الملف. تحقق من التنسيق والأعمدة.',
    rowsLoaded: 'صفوف محمّلة',
    summary: 'نجح {{ok}}، فشل {{failed}}',
    statusOk: 'تم',
    statusFailed: 'فشل',
    empty: 'لا توجد صفوف بعد. أضف صفًا أو ارفع ملفًا.',
    submitError: 'فشل الإرسال. حاول مرة أخرى.',
    orderNumberRequired: 'رقم الطلب مطلوب',
  },
};
